import { useState } from 'react';
import { useLocale } from 'hooks/useLocale';
import { AuthMethodType } from 'models/Auth';
import { SubscriptionType } from 'models/Subscription';
import {
  reportIdentity,
  reportUserClickedOnEvent,
  reportUserLoggedInEvent,
  reportUserRegisteredEvent,
  toSegmentIdentity,
} from 'utils/analytics/segment';
import useIsNewUser from 'networking/mindfeed/hooks/useIsNewUser';
import useSendOtp from 'networking/mindfeed/hooks/auth/useSendOtp';
import useVerifyOtp from 'networking/mindfeed/hooks/auth/useVerifyOtp';
import EmailForm from './forms/EmailForm';
import NameForm from './forms/NameForm';
import OtpForm from './forms/OtpForm';

export type EmailSignInStep = 'email' | 'name' | 'otp';

// The step is controlled so SignIn can keep the header and social providers
// beside the inline email field, and hide them once the flow moves on.
interface Props {
  step: EmailSignInStep;
  onStepChange: (step: EmailSignInStep) => void;
}

const EmailSignIn: React.FC<Props> = ({ step, onStepChange: setStep }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isNewAccount, setIsNewAccount] = useState(false);

  const { locale } = useLocale();
  const { mutateAsync: checkIsNewUser } = useIsNewUser();
  const { mutateAsync: sendOtp, isPending: isSending } = useSendOtp();
  const { mutateAsync: verifyOtp, isPending: isVerifying } = useVerifyOtp();

  const handleEmailSubmit = async (value: string) => {
    reportUserClickedOnEvent('Sign in with Email', 'Login Page');
    const isNew = await checkIsNewUser({ email: value });
    setEmail(value);
    setIsNewAccount(isNew);
    if (isNew) {
      setStep('name');
    } else {
      await sendOtp({ destination: value });
      setStep('otp');
    }
  };

  const handleNameSubmit = async (value: string) => {
    setName(value);
    await sendOtp({ destination: email, metadata: { name: value, locale } });
    setStep('otp');
  };

  // Analytics for the OAuth providers lives in AuthMachine, but this flow can't
  // report from there: it signs in with a custom token, so the machine only
  // learns about it via FIREBASE_AUTH_CHANGED — which is indistinguishable from
  // restoring an existing session on page load, and would emit a login on every
  // reload. Here the sign-in is unambiguous, and `isNewAccount` (from the API's
  // accountStatus) already tells us whether this is a registration.
  const handleOtpSubmit = async (code: string) => {
    const { user } = await verifyOtp({ email, code });

    reportIdentity(toSegmentIdentity(user, AuthMethodType.Email));

    if (isNewAccount) {
      reportUserRegisteredEvent(SubscriptionType.Free, AuthMethodType.Email);
    } else {
      reportUserLoggedInEvent(AuthMethodType.Email);
    }
  };

  // A registration resend must carry the same metadata as the first send, or
  // the account gets created without a name and locale.
  const handleResend = async () => {
    await sendOtp({
      destination: email,
      ...(isNewAccount && { metadata: { name, locale } }),
    });
  };

  if (step === 'otp') {
    return (
      <OtpForm
        email={email}
        isVerifying={isVerifying}
        isSending={isSending}
        onBack={() => setStep(isNewAccount ? 'name' : 'email')}
        onSubmit={handleOtpSubmit}
        onResend={handleResend}
      />
    );
  }

  if (step === 'name') {
    return (
      <NameForm
        email={email}
        onBack={() => setStep('email')}
        onSubmit={handleNameSubmit}
      />
    );
  }

  return <EmailForm defaultEmail={email} onSubmit={handleEmailSubmit} />;
};

export default EmailSignIn;
